<template>
  <div class="relative">
    <template v-if="compEarTags.length && listingIs.pedigree">
      <UtilDivider class="mt-5 mb-3" />

      <h3 v-if="listingIs.breeding" class="text-xl font-medium">{{ $t('titles.breedingData') }}</h3>

      <h3 v-else-if="listingIs.store" class="text-xl font-medium">{{ $t('titles.sires') }}</h3>

      <FormSelect
        v-if="compEarTags.length > 1"
        name="eartag"
        :label="$t('formFields.selectEarTag')"
        v-model="activeEarTag"
        :items="compEarTags"
        :emptyFirstOption="false"
        class="mt-2"
      />

      <p v-else class="my-4">
        Ear Tag:
        <span class="bg-gray-lightest px-2 ml-1 font-mono inline-block align-middle">
          {{ compEarTags[0].text }}
        </span>
      </p>

      <UtilIframe
        v-if="earTagUrl"
        :url="earTagUrl"
        :key="`url-${activeEarTag}`"
        iframeMinHeight="480"
        lazyLoad
        backgroundColor="#FFF"
        class="w-full mt-4"
      />

      <UtilIframe
        v-else
        :url="`${assetBaseUrl.hostedPage}/embed/animal/${activeEarTag}`"
        :key="activeEarTag"
        passToken
        passPath
        passApplicationId
        lazyLoad
        backgroundColor="#FFF"
        class="w-full mt-4"
      />
    </template>

    <template v-if="listing && listing.files && listing.files.length">
      <UtilDivider class="mt-5 mb-3" />

      <h3 class="text-xl font-medium">{{ $t('titles.animalData') }}</h3>

      <ListingFiles :listing="listing" class="mt-4 mb-5" />
    </template>

    <template v-if="!listingIs.pigs && (listingIs.cattle || compHealthData.length || diseases.length)">
      <UtilDivider class="mt-5 mb-3" />

      <h3 class="text-xl font-medium">{{ $t('titles.health') }}</h3>

      <template>
        <img
          v-if="compShowCoreVaccinated"
          v-lazy="require('@/assets/images/logos/services/core-vaccinated-logo-msd.png')"
          width="45"
          class="health-image"
          alt="Core Vaccinated"
        />
      </template>

      <template v-if="compHasSenseHubData">
        <UtilListingSenseHub :senseHubData="listing.meta.senseHub" class="mt-4" />

        <UtilDivider class="mt-4 mb-6" />
      </template>

      <template v-if="compHealthData">
        <table class="table-auto table-striped-alt w-full mt-2">
          <tbody>
            <tr v-for="item in compHealthData" :key="item.name">
              <td class="px-2 py-1 md:w-4/12">{{ item.name }}:</td>

              <td class="px-2 py-1 font-medium">
                {{ item.value }}
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <template
        v-if="
          listingIs.forSale &&
          (listingIs.cattle || listingIs.sheep) &&
          listing.meta.vetCheck !== '' &&
          !$store.getters['storage/hasKey']('hideVetCheck', true)
        "
      >
        <UtilTextLink @click="openVetCheck()" class="text-brand no-underline mt-6 text-lg block">
          <FontAwesomeIcon :icon="faNotesMedical" class="text-2xl mr-2" />
          <span class="underline">Get presale verification by an independent Vet with VetCheck</span>
        </UtilTextLink>
      </template>

      <template v-if="diseases.length">
        <table class="table-auto table-striped-alt w-full mt-8">
          <thead>
            <tr class="bg-gray-darker text-white text-center font-normal">
              <th class="px-2 text-left">Disease</th>
              <th class="px-2">
                Vaccinated
                <span class="block sm:inline md:block whitespace-no-wrap">or Treated</span>
              </th>
              <th class="px-2">
                Accredited
                <span class="block sm:inline md:block whitespace-no-wrap">or Monitored</span>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="disease in diseases" :key="disease.code">
              <td class="px-2 py-1">
                {{ disease.name.length > 20 ? disease.code.toUpperCase() : disease.name }}
              </td>

              <td class="px-2 py-1 text-green-700 font-medium text-center">
                <FontAwesomeIcon
                  v-if="
                    disease.vaccinated === 1 ||
                    (typeof disease.vaccinated === 'string' && disease.vaccinated !== '')
                  "
                  :icon="faCheckCircle"
                />

                <FontAwesomeIcon v-else-if="disease.vaccinated === 0" :icon="faTimes" class="text-danger" />

                <span v-else class="text-gray block text-center">-</span>
              </td>

              <td class="px-2 py-1 text-green-700 font-medium text-center">
                <FontAwesomeIcon
                  v-if="typeof disease.accredited === 'string' && disease.accredited !== ''"
                  :icon="faCheckCircle"
                />

                <FontAwesomeIcon v-else-if="disease.accredited === 1" :icon="faCheckCircle" />

                <FontAwesomeIcon v-else-if="disease.accredited === 0" :icon="faTimes" class="text-danger" />

                <span v-else class="text-gray block text-center">-</span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Core vaccinated -->
        <UtilMessage v-if="compShowCoreVaccinated" type="information" class="mt-8">
          <h3 class="inline-block mb-1 text-lg">Core Vaccinated</h3>
          <div class="sm:flex justify-between">
            <p>
              Core Vaccinated accreditation is there to provide peace of mind when buying-in stock, through knowing
              animals perform consistently and as expected. This ensures the vendor becomes a trusted source of
              animals that perform well.
            </p>
            <img
              v-lazy="require('@/assets/images/logos/services/core-vaccinated-logo-msd.png')"
              width="96"
              height="70"
              class="message-image my-2 sm:mt-0"
              alt="Core Vaccinated"
            />
          </div>
          <UtilTextLink @click="openCoreVaxLink()" class="block my-4 sm:my-0 text-brand">
            Found out more
          </UtilTextLink>
        </UtilMessage>

        <div v-if="compVaccinationProducts.length" class="flex flex-wrap mt-2">
          <div
            v-for="product in compVaccinationProducts"
            :key="product.vaccinationRef"
            class="flex flex-wrap items-center w-full sm:w-6/12 md:w-full lg:w-6/12 mb-4"
          >
            <div class="mr-2 text-center text-gray text-5xl" style="min-width: 50px">
              <UtilImage
                v-if="product.logoFileRef && product.advertising === 1"
                :fileRef="product.logoFileRef"
                :height="60"
                :isLazyLoaded="false"
              >
                <template #missingImage>
                  <FontAwesomeIcon :icon="faNotesMedical" />
                </template>
              </UtilImage>

              <FontAwesomeIcon v-else :icon="faNotesMedical" />
            </div>

            <div>
              <div class="text-gray-darker font-medium text-xs uppercase">{{ product.name }}</div>
              <div class="font-medium">{{ product.product }}</div>
              <div class="italic text-sm">{{ product.companyName }}</div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="listing.meta.vetCheck.includes('https://')">
        <h3 class="text-xl font-medium mt-4">{{ $t('titles.vetCheck') }}</h3>

        <p>This farm has had an independent VetCheck</p>

        <UtilButton @click="$openWindow(listing.meta.vetCheck, true)" color="secondary" class="mt-2">
          <FontAwesomeIcon :icon="faNotesMedical" />
          <span>Download VetCheck Report</span>
        </UtilButton>
      </template>

      <template v-if="listingIs.cattle && !listingIs.portalAuctionPartner && listing.location">
        <div class="font-medium bg-gray-darker text-white text-center mt-8">{{ $t('titles.tbMap') }}</div>

        <div class="tb-wrapper relative cursor-pointer">
          <UtilIframe :url="compTbMapUrl" :iframeMinHeight="255" lazyLoad class="w-full z-1" />

          <div class="tb-overlay absolute left-0 right-0 bottom-0 top-0 z-2"></div>

          <UtilButton
            @click="$modal.open('ModalTbMap', { iframeUrl: compTbMapUrl })"
            color="white"
            class="tb-button absolute position-center z-3"
          >
            <FontAwesomeIcon :icon="faMapMarkedAlt" />
            <span>View TB Map</span>
          </UtilButton>
        </div>
      </template>
    </template>

    <template v-if="listingIs.store && compPerformanceData.length">
      <UtilDivider class="mt-5 mb-3" />

      <h3 class="text-xl font-medium">{{ $t('titles.performance') }}</h3>

      <table class="table-auto table-striped-alt w-full mt-2">
        <tbody>
          <tr v-for="item in compPerformanceData" :key="item.name">
            <td class="px-2 py-1 md:w-4/12">{{ item.name }}:</td>

            <td class="px-2 py-1 font-medium">
              {{ item.value }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script>
  import {get} from 'lodash'
  import {
    faCheckCircle,
    faInfoCircle,
    faMapMarkedAlt,
    faNotesMedical,
    faTimes
  } from '@fortawesome/pro-regular-svg-icons'
  import speciesFromSexes from '@/lib/helpers/species-from-sexes'
  import tbInterval from '@/lib/helpers/tb-interval'
  import LegacyApi from '@/lib/api/legacy'
  import UtilIframe from '@/components/utils/UtilIframe'
  import UtilButton from '@/components/utils/UtilButton'
  import UtilDivider from '@/components/utils/UtilDivider'
  import UtilTextLink from '@/components/utils/UtilTextLink'
  import UtilImage from '@/components/utils/UtilImage'
  import FormSelect from '@/components/forms/elements/FormSelect'
  import ListingFiles from '@/views/ListingFiles'
  import UtilMessage from '@/components/utils/UtilMessage'

  export default {
  name: 'ListingDetailsLivestock',
  components: {
    UtilIframe,
    UtilButton,
    UtilDivider,
    UtilTextLink,
    UtilImage,
    FormSelect,
    ListingFiles,
    UtilMessage,
    UtilListingSenseHub: () => import('@/components/utils/listing/UtilListingSenseHub')
  },
  props: {
    isLoading: Boolean,
    listing: {
      type: Object,
      required: true
    },
    listingIs: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeEarTag: '',
      earTagUrl: null,
      diseases: [],
      faCheckCircle,
      faInfoCircle,
      faNotesMedical,
      faTimes,
      faMapMarkedAlt
    }
  },
  created() {
    this.fetchDiseases()
  },
  mounted() {
    this.showFirstEarTag()
  },
  computed: {
    compHealthData() {
      let data = [
        {
          name: this.$t('formFields.tbInterval'),
          value: tbInterval(this.listing.meta.tbInterval),
          active: this.listingIs.cattle
        },
        {
          name: this.$t('formFields.healthScheme'),
          value: this.listing.meta.premiumHealthScheme,
          active: this.listingIs.cattle && this.listing.meta.premiumHealthScheme
        },
        {
          // Legacy listings
          name: this.$t('formFields.healthScheme'),
          value: 'Yes',
          active:
            this.listingIs.legacy &&
            this.listingIs.cattle &&
            this.listing.meta.healthScheme &&
            !this.listing.meta.premiumHealthScheme
        },
        {
          name: this.$t('formFields.herdType'),
          value: this.listing.meta.herdType,
          active: this.listing.meta.herdType
        }
      ]

      return data.filter(item => item.active)
    },

    compPerformanceData() {
      let data = [
        {
          name: this.$t('formFields.conformation'),
          value: this.listing.meta.conformation,
          active: this.listing.meta.conformation
        },
        {
          name: this.$t('formFields.condition'),
          value: this.listing.meta.condition,
          active: this.listing.meta.condition
        },
        {
          name: this.$t('formFields.averageWeight'),
          value: this.listing.meta.averageWeight,
          active: this.listing.meta.averageWeight
        },
        {
          name: this.$t('formFields.dailyLiveWeightGain'),
          value: this.listing.meta.dlwg,
          active: this.listing.meta.dlwg !== '0.00'
        },
        {
          name: this.$t('formFields.feedingSystem'),
          value: this.listing.meta.feedingSystem ? this.listing.meta.feedingSystem.join(', ') : '',
          active: this.listing.meta.feedingSystem && this.listing.meta.feedingSystem.length
        }
      ]

      return data.filter(item => item.active)
    },

    compEarTagKey() {
      if (!this.listingIs.breeding && !this.listingIs.store) {
        return false
      }

      return this.listingIs.breeding ? 'livestock' : 'sires'
    },

    compEarTags() {
      let earTags = []

      if (this.listing.meta[this.compEarTagKey]) {
        this.listing.meta[this.compEarTagKey].forEach(tag => {
          if (tag.eartag) {
            earTags.push({
              text: tag.eartag,
              value: tag.eartag,
              url: tag.url?.length > 0 ? tag.url : null
            })
          }
        })
      }

      return earTags
    },

    compTbMapUrl() {
      let baseUrl =
        'https://www.ibtb.co.uk/default.aspx?sidebarshow=false&navbarshow=false&locationshow=false&breakdownstatus=ongoing&breakdownyear=all&zoom=9&consentRequired=false'

      return `${baseUrl}&y=${this.listing.location[0].latitude}&x=${this.listing.location[0].longitude}`
    },

    compVaccinationProducts() {
      if (!this.diseases.length) {
        return []
      }

      return this.diseases.filter(disease => disease.product)
    },

    compHasSenseHubData() {
      return !!get(this.listing, 'meta.senseHub.healthRate')
    },

    compShowCoreVaccinated() {
      if (Array.isArray(this.listing.meta.health) && this.listing.meta.health.length === 0) {
        return false
      }

      // if ageMax <= x && (Scour && BRD) || ageMax > x && (IBR && BVD && Lepto)
      if ((this.listingIs.cattle && !this.listingIs.store) || this.listingIs.calves) {
        if (
          this.listing.meta.ageMax <= 12 &&
          this.listing.meta.health['sco'].vaccinated &&
          this.listing.meta.health['brd'].vaccinated
        ) {
          return true
        } else if (
          this.listing.meta.ageMax > 12 &&
          this.listing.meta.health['bvd'].vaccinated &&
          this.listing.meta.health['ibr'].vaccinated &&
          this.listing.meta.health['lep'].vaccinated
        ) {
          return true
        }
      }

      if (this.listingIs.sheep) {
        if (
          this.listing.meta.ageMax <= 12 &&
          this.listing.meta.health['cv'].vaccinated &&
          this.listing.meta.health['fv'].vaccinated &&
          this.listing.meta.health['pta'].vaccinated
        ) {
          return true
        } else if (
          this.listing.meta.ageMax > 12 &&
          this.listing.meta.health['cv'].vaccinated &&
          this.listing.meta.health['eae'].vaccinated &&
          this.listing.meta.health['fr'].vaccinated &&
          this.listing.meta.health['pta'].vaccinated &&
          this.listing.meta.health['tox'].vaccinated
        ) {
          return true
        }
      }

      return false
    }
  },

  watch: {
    activeEarTag (newVal, oldVal) {
      if (newVal !== oldVal) {
        const earTag = this.compEarTags.find(earTag => earTag.value === newVal)

        this.earTagUrl = earTag.url
      }
    }
  },

  methods: {
    showFirstEarTag() {
      if (
        !this.listingIs.livestock ||
        !this.compEarTagKey ||
        !this.listing.meta[this.compEarTagKey] ||
        !this.listing.meta[this.compEarTagKey].length
      ) {
        return
      }

      this.activeEarTag = this.listing.meta[this.compEarTagKey][0].eartag
    },

    openVetCheck() {
      this.$analytics.addEvent({
        category: 'listing',
        action: 'Clicked VetCheck from listing health section'
      })

      this.$modal.open('ModalVetCheck', {
        listing: this.listing,
        listingIs: this.listingIs
      })
    },

    async fetchDiseases() {
      if (
        (!this.listingIs.cattle && !this.listingIs.sheep && !this.listingIs.pigs) ||
        !get(this.listing, 'meta.health') ||
        !this.listing.meta.sexes
      ) {
        return []
      }

      let diseaseVaccinationProducts = []
      let species = speciesFromSexes(this.listing.meta.sexes[0])

      // Some very old listings use categories that aren't in use anymore so we can't map them
      if (!species) {
        return []
      }

      let vaccinationApi = new LegacyApi({
        category: species
      })

      await vaccinationApi.get('vaccinations.get')

      // Flatten vaccination products
      vaccinationApi.data.forEach(vaccinationDisease =>
        vaccinationDisease.vaccinations.forEach(vaccination => diseaseVaccinationProducts.push(vaccination))
      )

      Object.keys(this.listing.meta.health).forEach(diseaseCode => {
        let disease = this.listing.meta.health[diseaseCode]

        // Before W, diseases were set to 0 by default, we need to force them to empty string (not answered)
        if (this.listingIs.legacy) {
          if (disease.vaccinated === 0) {
            disease.vaccinated = ''
          }

          if (disease.accredited === 0) {
            disease.accredited = ''
          }
        }

        if (diseaseCode === 'brd') {
          disease.name = 'Pneumonia/BRD'
        }

        let data = {
          ...disease,
          code: diseaseCode
        }

        if (disease.accredited === '' && disease.vaccinated === '') {
          return false
        }

        // If a vaccination ref is provided then match it up with products
        if (typeof disease.vaccinated === 'string') {
          let matchingProduct = diseaseVaccinationProducts.find(
            product => product.vaccinationRef === disease.vaccinated && product.advertising
          )

          if (matchingProduct) {
            data = {
              ...data,
              ...matchingProduct
            }
          }
        }

        this.diseases.push(data)
      })
    },
    openCoreVaxLink() {
      this.$openWindow(
        `${process.env.VUE_APP_SITE_URL}/blog/core-vaccination-in-the-ruminant-livestock-sector`,
        true
      )
    }
  }
}
</script>

<style lang="scss" scoped>
#app.mdAndUp .tb-wrapper:hover {
  .tb-overlay {
    @apply bg-black bg-opacity-50;
  }

  .tb-button {
    visibility: visible;
  }
}

#app.smAndDown .tb-overlay {
  @apply bg-black bg-opacity-50;
}

#app.mdAndUp .tb-button {
  visibility: hidden;
}

.health-image {
  position: absolute;
  top: 4px;
  right: 20px;
}
.message-image {
  max-width: 60px;
  position: absolute;
  bottom: 0;
  right: 20px;

  @media screen and (min-device-width: 667px) {
    position: initial;
    max-width: 70px;
    max-height: 70px;
  }
}
</style>
